/**
 * What a roll means, as data.
 *
 * `interpretResult` is the method that decides whether a number was a success,
 * and in a code adapter it is a switch statement its author wrote. A
 * declarative system has no author on hand at runtime, so the decision has to
 * be a shape — and the shape has to cover four genuinely different games:
 *
 *   d20 vs DC    total >= dc, natural 20 always the best, natural 1 the worst
 *   roll-under   total <= the ability score; high is bad
 *   2d6 PbtA     10+ success, 7-9 partial, 6- failure, and NO target number
 *   4dF Fate     total vs a difficulty on a named ladder
 *
 * THE ONE SHAPE: an ordered list of rungs, each a comparison and an outcome,
 * evaluated top to bottom, first match wins. A rung compares one of three
 * measures — the natural dice, the modified total, or the margin between total
 * and target — against either a fixed number or the target itself.
 *
 * That is enough for all four. d20 puts its natural-20 rung first so it outranks
 * the DC comparison below it. Roll-under flips the operator. PbtA compares the
 * total to fixed numbers and never mentions a target, which is why `target` is
 * optional rather than assumed. PF2e's four degrees fall out of `margin`.
 *
 * WHY FIRST-MATCH-WINS RATHER THAN SCORING: order is the rule. "A natural 20 is
 * a critical success even against a DC you cannot reach" is a statement about
 * precedence, and a ladder that evaluates in order says it directly, where a
 * scored model would need a separate priority field saying the same thing less
 * clearly.
 *
 * WHAT THIS DELIBERATELY CANNOT DO: consult character state, spend a resource,
 * ask a player a question, or pause. A resolution that PAUSES is the Tier 2 /
 * Tier 3 line — Doomsworn's Doom Die waits for a decision after the dice land,
 * and no ladder models that. Those systems stay code adapters.
 */
import { z } from 'zod';
// =============================================================================
// SCHEMA
// =============================================================================
/** Which number a rung looks at. */
export const LadderMeasureSchema = z.enum([
    /** The dice before modifiers — where "natural 20" lives. */
    'natural',
    /** Dice plus modifiers. PbtA and most d20 comparisons. */
    'total',
    /** `total - target`. Degrees of success, PF2e's crit-by-10. */
    'margin',
]);
export const LadderOperatorSchema = z.enum(['>=', '>', '<=', '<', '==', '!=']);
/**
 * What a rung compares against: a fixed number, or the roll's target.
 *
 * `'target'` is a string rather than a magic number because a rules document is
 * read by humans, and `{ measure: 'total', op: '>=', value: 'target' }` says
 * "beat the DC" where a sentinel like -1 would say nothing.
 */
export const LadderValueSchema = z.union([z.number(), z.literal('target')]);
export const LadderOutcomeSchema = z.enum([
    'critical-success',
    'success',
    'partial',
    'failure',
    'critical-failure',
]);
export const LadderRungSchema = z.object({
    measure: LadderMeasureSchema,
    op: LadderOperatorSchema,
    value: LadderValueSchema,
    outcome: LadderOutcomeSchema,
    /**
     * How the table hears it. `{total}`, `{natural}`, `{target}` and `{margin}`
     * are substituted.
     *
     * Authored rather than generated, because "You mark a Ruin" is the sentence a
     * Trophy player needs and "Success (14 vs DC 12)" is not.
     */
    description: z.string(),
    /**
     * Restricts this rung to certain `RollContext.type` values — "attack",
     * "saving-throw". Absent means every roll.
     *
     * This exists because of a real bug: 5e's adapter returns critical-success on
     * a natural 20 for EVERY context type, so routing saving throws through it
     * made a nat 20 auto-pass any save. That is the 2024 rule stated by accident
     * (see ISS-0150). A declarative system has to be able to say which rolls its
     * crit rung applies to, or it will make the same mistake silently.
     */
    appliesTo: z.array(z.string()).optional(),
});
export const OutcomeLadderSchema = z.object({
    /**
     * Rungs in precedence order. The first whose comparison holds decides the
     * outcome, so specific rules go above general ones.
     */
    rungs: z.array(LadderRungSchema).min(1),
    /** Used when no rung matches. Every ladder needs a floor. */
    fallback: z.object({
        outcome: LadderOutcomeSchema,
        description: z.string(),
    }),
    /**
     * The target used when a roll carries none.
     *
     * Optional on purpose. A PbtA ladder never mentions a target, so it needs no
     * default; a d20 ladder rolled with no DC has nothing honest to compare
     * against, and the funnel already withholds interpretation in that case. A
     * system that genuinely has a standing difficulty says so here.
     */
    defaultTarget: z.number().optional(),
});
// =============================================================================
// EVALUATION
// =============================================================================
function compare(left, op, right) {
    switch (op) {
        case '>=':
            return left >= right;
        case '>':
            return left > right;
        case '<=':
            return left <= right;
        case '<':
            return left < right;
        case '==':
            return left === right;
        case '!=':
            return left !== right;
    }
}
function fill(template, values) {
    return template.replace(/\{(\w+)\}/g, (whole, key) => {
        const value = values[key];
        return value === undefined ? whole : String(value);
    });
}
/**
 * Read a roll against a ladder.
 *
 * Returns null when a rung the roll needs cannot be evaluated — specifically,
 * when the ladder wants a target and the roll has none. That is the same
 * refusal `resolveRoll` makes: a check with no difficulty set is a number
 * waiting for a GM, and inventing one produces a confident outcome against a
 * DC nobody chose.
 */
export function interpretAgainstLadder(ladder, roll, context) {
    const target = context.dc ?? ladder.defaultTarget;
    const measures = {
        natural: roll.natural,
        total: roll.total,
        margin: target === undefined ? undefined : roll.total - target,
    };
    for (const rung of ladder.rungs) {
        if (rung.appliesTo && !rung.appliesTo.includes(context.type))
            continue;
        const left = measures[rung.measure];
        if (left === undefined)
            continue; // needs a target it does not have
        const right = rung.value === 'target' ? target : rung.value;
        if (right === undefined)
            continue;
        if (compare(left, rung.op, right)) {
            return {
                outcome: rung.outcome,
                description: fill(rung.description, {
                    natural: roll.natural,
                    total: roll.total,
                    target,
                    margin: measures.margin,
                }),
            };
        }
    }
    // A ladder that skipped a rung for want of a target the roll did not carry
    // has not failed — it has declined, and the caller must not report a failure
    // the system never reached. Distinguishing the two is why this returns null
    // rather than the fallback in that case.
    //
    // ANY skipped rung, not all of them. The roll-under ladder reads its natural
    // die for crits with fixed numbers and only its success rung against the
    // target, so an "every rung needs a target" test says no and the fallback
    // fires: a Cairn check rolled before the GM sets a difficulty comes back
    // "Failure (11 over undefined)". The rungs that DID resolve still get their
    // say — a natural 1 is a critical success whether or not anyone named a
    // target — and this only catches the case where the answer hinged on a
    // comparison that could not be made.
    const skippedForTarget = target === undefined &&
        ladder.rungs.some((rung) => rung.value === 'target' || rung.measure === 'margin');
    if (skippedForTarget)
        return null;
    return {
        outcome: ladder.fallback.outcome,
        description: fill(ladder.fallback.description, {
            natural: roll.natural,
            total: roll.total,
            target,
            margin: measures.margin,
        }),
    };
}
// =============================================================================
// READY-MADE LADDERS
// =============================================================================
/**
 * The d20 ladder, matching what `@machina/system-dnd5e` does in TypeScript.
 *
 * Kept here as the reference the differential test in ISS-0149 measures the
 * declarative runtime against: if this ladder and the hand-written adapter
 * disagree on any natural × DC pair, one of them is wrong.
 *
 * NOTE THE `appliesTo` ABSENCE ON THE CRIT RUNGS: this reproduces 5e's current
 * behaviour, including the nat-20-passes-any-save reading that ISS-0150 is open
 * about. It is written this way so the differential test compares like with
 * like; when ISS-0150 is decided, both move together.
 */
export const D20_ROLL_HIGH_LADDER = {
    rungs: [
        {
            measure: 'natural',
            op: '==',
            value: 20,
            outcome: 'critical-success',
            description: 'Natural 20! Critical success (total {total} vs DC {target})',
        },
        {
            measure: 'natural',
            op: '==',
            value: 1,
            outcome: 'critical-failure',
            description: 'Natural 1! Critical failure (total {total} vs DC {target})',
        },
        {
            measure: 'total',
            op: '>=',
            value: 'target',
            outcome: 'success',
            description: 'Success ({total} vs DC {target})',
        },
    ],
    fallback: { outcome: 'failure', description: 'Failure ({total} vs DC {target})' },
    // No default target. The 5e adapter used to invent a DC of 10 and this
    // matched it; both now decline instead, so a d20 roll made before anyone set
    // a difficulty reports its criticality and nothing else.
};
/** Roll-under: the ability score is the target and low is good. */
export const D20_ROLL_UNDER_LADDER = {
    rungs: [
        {
            measure: 'natural',
            op: '==',
            value: 1,
            outcome: 'critical-success',
            description: 'Natural 1 — the best it gets ({total} under {target})',
        },
        {
            measure: 'natural',
            op: '==',
            value: 20,
            outcome: 'critical-failure',
            description: 'Natural 20 — the worst it gets ({total} over {target})',
        },
        {
            measure: 'total',
            op: '<=',
            value: 'target',
            outcome: 'success',
            description: 'Success ({total} under {target})',
        },
    ],
    fallback: { outcome: 'failure', description: 'Failure ({total} over {target})' },
};
/**
 * Powered by the Apocalypse: 10+, 7-9, 6-.
 *
 * The ladder that proves the model, because it has no target number at all.
 * Every rung compares the total to a fixed threshold, so `context.dc` is never
 * consulted and never missed.
 */
export const PBTA_2D6_LADDER = {
    rungs: [
        {
            measure: 'total',
            op: '>=',
            value: 10,
            outcome: 'success',
            description: 'You do it ({total})',
        },
        {
            measure: 'total',
            op: '>=',
            value: 7,
            outcome: 'partial',
            description: 'You do it, but ({total}) — the GM picks a cost',
        },
    ],
    fallback: { outcome: 'failure', description: 'It goes wrong ({total}) — the GM makes a move' },
};
//# sourceMappingURL=ladder.js.map